(() => {
  const communities = ['howell', 'brighton', 'hartland', 'fowlerville', 'pinckney'];
  const key = 'landos-community';
  const defaultCommunity = 'howell';
  const valid = (value) => communities.includes(value) ? value : null;
  const requested = () => valid(new URL(location.href).searchParams.get('community')?.toLowerCase());
  // Start new visits on Howell; retain a shopper's choice within this tab.
  const remembered = () => { try { return valid(sessionStorage.getItem(key)); } catch { return null; } };
  const remember = (value) => { try { sessionStorage.setItem(key, value); } catch {} };
  const displayName = (value) => value.charAt(0).toUpperCase() + value.slice(1);
  const updateNavigation = (value) => {
    document.querySelectorAll('[data-landos-shop-link]').forEach((link) => {
      const url = new URL(link.href);
      url.searchParams.set('community', value);
      link.href = url.href;
    });
    document.querySelectorAll('[data-landos-community-link]').forEach((link) => {
      if (link.dataset.landosCommunityLink === value) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  };

  class LandosNavigation extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      const close = (except, focus = false) => this.querySelectorAll('details[open]').forEach((menu) => {
        if (menu !== except) { menu.open = false; if (focus) menu.querySelector('summary').focus(); }
      });
      this.querySelectorAll('details').forEach((menu) => menu.addEventListener('toggle', () => { if (menu.open) close(menu); }, options));
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link) return;
        if (link.dataset.landosCommunityLink) remember(link.dataset.landosCommunityLink);
        close();
      }, options);
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) close(); }, options);
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(null, true); }, options);
      document.addEventListener('cart:update', (event) => {
        const count = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;
        if (!Number.isFinite(count)) return;
        const badge = this.querySelector('[data-landos-cart-count]');
        badge.textContent = count;
        badge.hidden = count === 0;
      }, options);
      updateNavigation(requested() || remembered() || defaultCommunity);
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class LandosCommunityShop extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.cards = [...this.querySelectorAll('[data-landos-card]')].map((element) => ({
        element,
        variants: JSON.parse(element.querySelector('[data-community-variants]').textContent),
        tags: element.dataset.communityTags.trim().split(/\s+/).filter(Boolean),
        hasCommunity: Number(element.dataset.communityPosition) > 0
      }));
      this.addEventListener('change', (event) => {
        if (event.target.matches('.landos-community-choices input')) this.select(event.target.value, true);
        if (event.target.matches('[name="sort_by"]')) event.target.form.requestSubmit();
      }, options);
      if (this.dataset.hasCommunities === 'false') return;
      window.addEventListener('popstate', () => this.select(requested() || valid(this.dataset.defaultCommunity) || remembered() || defaultCommunity), options);
      this.select(requested() || valid(this.dataset.defaultCommunity) || remembered() || defaultCommunity);
    }
    disconnectedCallback() { this.abort?.abort(); }
    select(value, updateUrl = false) {
      if (!valid(value)) return;
      // Each community has its own server-rendered page and canonical URL.
      if (this.dataset.communityLanding === 'true' && value !== this.dataset.defaultCommunity) {
        const url = new URL(`${this.dataset.communityRoot}${value}`, location.origin);
        const sort = new URL(location.href).searchParams.get('sort_by');
        if (sort) url.searchParams.set('sort_by', sort);
        remember(value);
        location.assign(url.href);
        return;
      }
      remember(value);
      updateNavigation(value);
      this.querySelectorAll('.landos-community-choices input').forEach((radio) => { radio.checked = radio.value === value; });
      const communityField = this.querySelector('[name="community"]');
      if (communityField) communityField.value = value;
      let count = 0;
      for (const card of this.cards) {
        const variant = card.hasCommunity ? card.variants.find((variant) => variant.community === value) : card.variants[0];
        card.element.hidden = !variant || (!card.hasCommunity && card.tags.length > 0 && !card.tags.includes(value));
        if (card.element.hidden) continue;
        count++;
        const link = card.element.querySelector('.landos-shop-card-link');
        const url = new URL(variant.url, location.origin);
        if (card.hasCommunity || card.tags.length > 0) url.searchParams.set('community', value);
        link.href = url.href;
        card.element.querySelector('[data-card-price]').innerHTML = variant.price;
        card.element.querySelector('[data-card-soldout]').hidden = variant.available;
        const image = card.element.querySelector('img');
        if (image && variant.image) {
          const imageUrl = new URL(variant.image, location.origin);
          if (image.src !== imageUrl.href) {
            image.classList.add('landos-image-loading');
            image.onload = () => image.classList.remove('landos-image-loading');
            image.onerror = () => image.classList.remove('landos-image-loading');
            const sourceSet = [300, 450, 600, 900].map((width) => { const url = new URL(imageUrl); url.searchParams.set('width', width); return `${url.href} ${width}w`; }).join(', ');
            image.srcset = sourceSet;
            image.src = imageUrl.href;
            if (image.complete) image.classList.remove('landos-image-loading');
          }
          image.alt = `${card.element.dataset.productTitle}${card.hasCommunity ? ` — ${displayName(value)}` : ''}`;
        }
      }
      const countLabel = this.querySelector('[data-product-count]');
      if (countLabel) countLabel.textContent = this.dataset.countTemplate.replace('[count]', count);
      this.querySelector('[data-community-empty]').hidden = count > 0;
      this.querySelector('[data-community-status]').textContent = this.dataset.statusTemplate.replace('[community]', displayName(value));
      if (this.dataset.communityLanding !== 'true') this.querySelectorAll('.landos-shop-pagination a').forEach((link) => { const url = new URL(link.href); url.searchParams.set('community', value); link.href = url.href; });
      if (updateUrl && this.dataset.communityLanding !== 'true') {
        const url = new URL(location.href);
        url.searchParams.set('community', value);
        history.replaceState(history.state, '', url);
      }
    }
  }

  if (!customElements.get('landos-navigation')) customElements.define('landos-navigation', LandosNavigation);
  if (!customElements.get('landos-community-shop')) customElements.define('landos-community-shop', LandosCommunityShop);

  const mainPicker = () => [...document.querySelectorAll('variant-picker[data-template-product-match="true"]')].find((picker) => !picker.closest('quick-add-dialog, product-card'));
  const saveProductCommunity = () => {
    const selected = mainPicker()?.querySelector('input[data-landos-community]:checked');
    if (!selected || !valid(selected.dataset.landosCommunity)) return;
    const value = selected.dataset.landosCommunity;
    remember(value);
    updateNavigation(value);
    const url = new URL(location.href);
    if (url.searchParams.has('community')) { url.searchParams.set('community', value); history.replaceState(history.state, '', url); }
  };
  document.addEventListener('change', (event) => {
    if (event.target.matches('input[data-landos-community]') && mainPicker()?.contains(event.target)) saveProductCommunity();
  });
  document.addEventListener('variant:update', saveProductCommunity);
  const initializeProduct = async () => {
    const picker = mainPicker();
    if (!picker?.querySelector('[data-landos-community]')) return;
    await customElements.whenDefined('variant-picker');
    const url = new URL(location.href);
    if (url.searchParams.has('variant') || url.searchParams.has('option_values')) { saveProductCommunity(); return; }
    const desired = requested() || remembered() || defaultCommunity;
    const target = desired && picker.querySelector(`input[data-landos-community="${desired}"]`);
    if (target && !target.checked) target.click();
    else saveProductCommunity();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeProduct, {once: true});
  else initializeProduct();
})();
